import EditIcon from '@mui/icons-material/Edit';
import { Box, Card, CardContent, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { useContext, useMemo } from 'react';
import { formatPhoneNumber, formatPhoneNumberIntl } from 'react-phone-number-input';
import AdditionalContactInfo from '@/components/common/contact_components/AdditionalContactInfo';
import RequiredIndicator from '@/components/common/RequiredIndicator';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { SelectedProgramContext } from '@/Contexts/SelectedProgramContext';
import { ContactConfig, ContactProps, ContactType } from '@/models/Contact';
import { ContactDepartment } from '@/models/ContactDepartment';
import { CountryProps } from '@/models/Country';
import { CountyProps } from '@/models/County';
import { DepartmentTypeProps } from '@/models/DepartmentType';
import { ProgramNames } from '@/models/Program';
import { StateProps } from '@/models/State';
import { USCountryId } from '@/utils/constants';
import { getAddressValidation, getContactHasError } from '@/utils/contactValidationUtil';
import { stringHasValue } from '@/utils/stringUtil';
import { phoneNumberIsValid } from '@/utils/contactUtils';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';

function getIsUnitedStatesContact(
  selectedProgramId: ProgramNames,
  countryId: ContactProps['countryId']
): boolean {
  return selectedProgramId !== ProgramNames.TreeCitiesWorld || countryId === USCountryId;
}

function getStateAbbrev(
  isUnitedStatesContact: boolean,
  stateId: ContactProps['stateId'],
  allStates: StateProps[]
): string | undefined {
  if (!isUnitedStatesContact || !stringHasValue(stateId)) return undefined;
  return allStates.find((s) => s.id === stateId)?.abbrev;
}

function getCountyName(
  isUnitedStatesContact: boolean,
  countyId: ContactProps['countyId'],
  stateId: ContactProps['stateId'],
  allCounties: CountyProps[]
): string | undefined {
  if (!isUnitedStatesContact || !stringHasValue(countyId) || !stringHasValue(stateId))
    return undefined;
  return allCounties.find((c) => c.id === countyId && c.stateId === stateId)?.name;
}

function getCountryName(
  countryId: ContactProps['countryId'],
  allCountries: CountryProps[]
): string | undefined {
  if (countryId == null) return undefined;
  return allCountries.find((c) => c.id === countryId)?.name;
}

function getContactDepartments(
  contactTypes: ContactType[],
  contactConfigId: ContactConfig['id']
): ContactDepartment[] {
  return contactTypes.find((c) => c.id === contactConfigId)?.departmentTypes ?? [];
}

function getDepartmentName(
  contact: ContactProps,
  contactConfig: ContactConfig,
  contactDepartments: ContactDepartment[],
  departmentTypes: DepartmentTypeProps[] | undefined
): string | undefined {
  if (!contactConfig.displayDepartment) return undefined;
  if (contact.departmentTypeId == null) return undefined;

  const inContactType = contactDepartments.find(
    (d: ContactDepartment) => d.id === contact.departmentTypeId
  )?.name;

  const inGlobal = departmentTypes?.find(
    (d: DepartmentTypeProps) => d.id === contact.departmentTypeId
  )?.name;

  const dept = inContactType ?? inGlobal;
  if (dept == null) return undefined;

  if (dept.toLowerCase() === 'other' && stringHasValue(contact.departmentOther))
    return contact.departmentOther ?? undefined;
  return dept;
}

function getFormattedPhoneNumber(
  phoneUnvalidated: string | null | undefined,
  countryId: number | null | undefined
): string | undefined {
  if (!stringHasValue(phoneUnvalidated)) return undefined;

  const phone = phoneUnvalidated;
  const p = phone[0] !== '+' ? `+${phone}` : phone;
  return countryId === USCountryId ? formatPhoneNumber(p) : formatPhoneNumberIntl(p);
}

const renderRequiredLine = (label: string) => (
  <Typography color='error'>
    {label} <RequiredIndicator />
  </Typography>
);

const ContactCard = ({
  contact,
  contactConfig,
  onEditRequested,
  showEditButton = true,
  showDuplicateWarning = false,
}: {
  contact: ContactProps;
  contactConfig: ContactConfig;
  onEditRequested: () => void;
  showEditButton?: boolean;
  showDuplicateWarning?: boolean;
}) => {
  const theme = useTheme();
  const { canEdit } = useContext(CurrentApplicationContext);
  const { selectedProgramId } = useContext(SelectedProgramContext);
  const { translatedText } = useContext(LocaleContext);
  const { allStates, allCountries, allCounties, contactTypes, departmentTypes } =
    useContext(ConfigContext);
  const isUnitedStatesContact = getIsUnitedStatesContact(selectedProgramId, contact.countryId);
  const stateAbbrev = getStateAbbrev(isUnitedStatesContact, contact.stateId, allStates);
  const countyName = getCountyName(
    isUnitedStatesContact,
    contact.countyId,
    contact.stateId,
    allCounties
  );
  const countryName = getCountryName(contact.countryId, allCountries);
  const contactDepartments = getContactDepartments(contactTypes, contactConfig.id);
  const departmentName = getDepartmentName(
    contact,
    contactConfig,
    contactDepartments,
    departmentTypes
  );
  const formattedPhoneNumber = getFormattedPhoneNumber(contact.phone, contact.countryId);
  const contactHasError = getContactHasError(contact, contactConfig, selectedProgramId);
  const addressValidation = useMemo(
    () => getAddressValidation(contact, selectedProgramId),
    [contact, selectedProgramId]
  );
  const addressErrors = useMemo(() => {
    const errors: string[] = [];
    if (!contactConfig.displayAddress) {
      return errors;
    }

    if (addressValidation.missingFields.includes('streetAddress')) {
      errors.push(
        `${translatedText.address.labelStreetAddress} ${translatedText.contact.requiredFieldSuffix}`
      );
    }
    if (addressValidation.missingFields.includes('city')) {
      errors.push(
        `${translatedText.address.labelCity} ${translatedText.contact.requiredFieldSuffix}`
      );
    }
    if (addressValidation.missingFields.includes('state')) {
      errors.push(
        `${translatedText.address.labelState} ${translatedText.contact.requiredFieldSuffix}`
      );
    }
    if (addressValidation.missingFields.includes('zipCode')) {
      errors.push(
        `${translatedText.address.labelZipCode} ${translatedText.contact.requiredFieldSuffix}`
      );
    }
    if (addressValidation.missingFields.includes('county')) {
      errors.push(
        `${translatedText.address.labelCounty} ${translatedText.contact.requiredFieldSuffix}`
      );
    }
    if (addressValidation.missingFields.includes('country')) {
      errors.push(
        `${translatedText.address.labelCountry} ${translatedText.contact.requiredFieldSuffix}`
      );
    }
    if (addressValidation.hasInvalidZipCode) {
      errors.push(translatedText.contact.invalidZipCode);
    }

    return errors;
  }, [addressValidation, contactConfig.displayAddress, translatedText]);

  return (
    <Card
      sx={{
        border: contactHasError
          ? `1px solid ${theme.palette.error.main}`
          : `1px solid ${theme.palette.sandShade3.main}`,
        width: '100%',
      }}
    >
      <CardContent
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <Box display='flex' alignItems='center' gap='8px'>
          <Typography variant='h4' sx={{ marginBottom: 0 }}>
            {`${contact.firstName} ${contact.lastName}`}
          </Typography>
          {showDuplicateWarning && (
            <Box
              sx={{
                display: 'inline-flex',
                gap: '4px',
                alignItems: 'center',
                padding: '4px 8px',
                borderRadius: '16px',
                backgroundColor: theme.palette.warningBackground.main,
                color: theme.palette.warningBackground.contrastText,
              }}
            >
              <Box
                sx={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '100%',
                  backgroundColor: theme.palette.warningBackground.contrastText,
                }}
              ></Box>
              <Typography variant='subtitle3'>Possible Duplicate</Typography>
            </Box>
          )}
        </Box>

        {showEditButton && (
          <IconButton
            size='small'
            aria-label='edit'
            disabled={!canEdit}
            onClick={onEditRequested}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <EditIcon />
          </IconButton>
        )}

        {contactConfig.displayTitle && (
          <AdditionalContactInfo title={translatedText.contact.labelTitle} value={contact.title} />
        )}

        {contactConfig.displayAddress && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant='overline2' component='p' sx={{ mb: 0.5 }}>
                Address
              </Typography>
              {addressErrors.length > 0 && (
                <Tooltip title={addressErrors.join(', ')} arrow>
                  <Box
                    component='span'
                    sx={{
                      marginLeft: '4px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      color: 'error.main',
                    }}
                  >
                    <ErrorOutlineIcon aria-label='Invalid field details' sx={{ fontSize: 16 }} />
                  </Box>
                </Tooltip>
              )}
            </Box>

            {stringHasValue(contact.streetAddress1) ? (
              <Typography color='textSecondary'>{contact.streetAddress1}</Typography>
            ) : (
              renderRequiredLine(translatedText.address.labelStreetAddress)
            )}

            {stringHasValue(contact.streetAddress2) && (
              <Typography color='textSecondary'>{contact.streetAddress2}</Typography>
            )}

            {/* City / State / Zip line */}
            <Typography color='textSecondary'>
              {stringHasValue(contact.city) ? `${contact.city}, ` : ''}
              {isUnitedStatesContact ? (stringHasValue(stateAbbrev) ? `${stateAbbrev} ` : '') : ''}
              <span
                style={{
                  color: addressValidation.hasInvalidZipCode
                    ? theme.palette.error.main
                    : theme.palette.text.secondary,
                }}
              >
                {contact.zipCode ?? ''}
              </span>
            </Typography>

            {/* Required markers for missing city/state/zip, shown as their own lines */}
            {!stringHasValue(contact.city) && renderRequiredLine(translatedText.address.labelCity)}
            {isUnitedStatesContact &&
              !stringHasValue(stateAbbrev) &&
              renderRequiredLine(translatedText.address.labelState)}
            {!stringHasValue(contact.zipCode) &&
              renderRequiredLine(translatedText.address.labelZipCode)}

            {isUnitedStatesContact &&
              (stringHasValue(countyName) ? (
                <Typography color='textSecondary'>{countyName}</Typography>
              ) : (
                renderRequiredLine(translatedText.address.labelCounty)
              ))}

            {stringHasValue(countryName) ? (
              <Typography color='textSecondary'>{countryName}</Typography>
            ) : (
              renderRequiredLine(translatedText.address.labelCountry)
            )}
          </Box>
        )}

        {contactConfig.displayEmail && (
          <AdditionalContactInfo title={translatedText.contact.labelEmail} value={contact.email} />
        )}

        {contactConfig.displayPhone && (
          <AdditionalContactInfo
            title={translatedText.contact.labelPhoneNumber}
            value={formattedPhoneNumber}
            hasError={!stringHasValue(contact.phone) || !phoneNumberIsValid(contact.phone)}
            errorHelperText={
              !phoneNumberIsValid(contact?.phone ?? null)
                ? translatedText.contact.invalidPhoneNumber
                : undefined
            }
          />
        )}

        {contactConfig.displayDepartment && stringHasValue(departmentName) && (
          <AdditionalContactInfo title='Department' value={departmentName} />
        )}

        {contactConfig.displayCertification && stringHasValue(contact.certification) && (
          <AdditionalContactInfo title='Certification' value={contact.certification} />
        )}

        {contactConfig.displayWhatsapp &&
          stringHasValue(contact.whatsApp) &&
          selectedProgramId === ProgramNames.TreeCitiesWorld &&
          contact.countryId !== USCountryId && (
            <AdditionalContactInfo title='WhatsApp' value={contact.whatsApp} />
          )}

        {contactConfig.displayLinkedIn && stringHasValue(contact.linkedIn) && (
          <AdditionalContactInfo title='LinkedIn' value={contact.linkedIn} />
        )}
      </CardContent>
    </Card>
  );
};

export default ContactCard;
